import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import { useLiveQuery } from 'dexie-react-hooks';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { db } from '../db';
import { ProgressBar } from 'components/ui';
import { AssessmentType, Drugs, AssessmentResultType } from 'types';
import { Loader } from 'components/ui/Loader';
import { useAuth } from './AuthContextProvider';
import { BaseButton } from 'components/ui/buttons';
import { useTenancy } from './TenantContextProvider';
import { ErrorModal } from 'components/ui/modal/ErrorModal';
import { AlertIcon, LeftIcon, RightIcon } from 'assets/icons';
import { AssessmentDataRecord } from 'record/assessment-data.record';
import {
  getAssessment,
  postCheckDrugChanges,
  postClonidineTrialedOffByPatientId,
  getPatientInformation,
  getLastAssessmentResultsByPatientId,
  postAssessmentResult,
} from 'services/api';
import { Narcotics } from 'record/onboarding-result.record';
import { MessageModal } from 'components/ui/modal/MessageModal';
import { useNetworkCheck } from 'contexts/NetworkContextProvider';
import { PatientMedicalRecord } from 'record/patient-medical.record';
import { MorphineRecommendation } from 'algorithms/morphine-algorithm';
import { AssessmentResultRecord, MissingAssessmentRecord } from 'record/assessment-result.record';
import { ClonidineRecommendation } from 'algorithms/clonidine-algorithm';
import { AppRoutes, replaceRouteParamWithValue } from 'routes/app-routes';
import { DrugChangesResultRecord } from 'record/drug-changes-result.record';
import { AssessmentHeader } from 'components/assessment/common/AssessmentHeader';
import { MainAssessmentQuestionnaire } from 'components/assessment/MainAssessmentQuestionnaire';
import { CommonQuestionnaire, CommonQuestionSteps } from 'components/assessment/CommonQuestionnaire';
import { AssessProAlgoResult, executeAssessProAlgorithm } from 'algorithms/assesspro-algorithm';
import cn from 'classnames';
import { determineEvaluationScore } from 'algorithms/determine-evaluation-score-algorithm';
import { ReviewAssessmentStep } from 'components/assessment/steps/ReviewAssessmentStep';

type StepDataType = {
  current: number;
  completedOrdered: number[];
};

type AssessmentContextType = {
  currentStep: number;
  assessment: AssessmentType | null;
  assessmentData: AssessmentDataRecord;
  assessmentResult: AssessmentResultRecord | null;
  setAssessmentData: React.Dispatch<React.SetStateAction<AssessmentDataRecord>>;
  setAssessmentResult: React.Dispatch<React.SetStateAction<AssessmentResultRecord>>;
};

type AssessmentProviderProps = {
  children: ReactNode;
};

type CommonAssessmentType = {
  handleNextStep: () => number | null;
};

type MainAssessmentType = {
  handleNextStep: () => number | null;
};

const LENGTH_COMMON_QUESTIONNAIRE = Object.keys(CommonQuestionSteps).length / 2;

const MAX_HOURS_GAP_ASSESSMENT = 6;
const MAIN_ASSESSMENT_START_STEP = LENGTH_COMMON_QUESTIONNAIRE + 1;

const AssessmentContext = createContext<AssessmentContextType | null>(null);

type CurrentDosesRouteState = {
  currentMorphineDose: number;
  currentClonidineDose: number;
  currentClonidineFrequency: number;
};

const isCurrentDosesRouteState = (state: unknown): state is CurrentDosesRouteState => {
  if (!state || typeof state !== 'object') return false;

  const candidate = state as Partial<CurrentDosesRouteState>;

  return (
    typeof candidate.currentMorphineDose === 'number' &&
    typeof candidate.currentClonidineDose === 'number' &&
    typeof candidate.currentClonidineFrequency === 'number'
  );
};

export const AssessmentProvider: React.FC<AssessmentProviderProps> = ({ children }) => {
  // Assessment Questions/AnswerOptions
  const [assessment, setAssessment] = useState<AssessmentType | null>(null);
  // Assessment Data for all workflows
  const [assessmentData, setAssessmentData] = useState<AssessmentDataRecord>(new AssessmentDataRecord());
  // Current Assessment Result (to be sent to API)
  const [assessmentResult, setAssessmentResult] = useState<AssessmentResultRecord>(new AssessmentResultRecord());
  const [patientMedicalData, setPatientMedicalData] = useState<PatientMedicalRecord | null>(null);
  const [startStep, setStartStep] = useState<number>(CommonQuestionSteps.BirthWeight);
  const [stepsData, setStepsData] = useState<StepDataType>({
    current: CommonQuestionSteps.BirthWeight,
    completedOrdered: [],
  });

  const [isSubmitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isOfflineWarningModalOpen, setOfflineWarningModalOpen] = useState(false);

  const assessmentFromDB = useLiveQuery(() => db.assessment.toArray());

  const navigate = useNavigate();

  const tenantKey = useTenancy();
  const { user } = useAuth();

  const { id: patientId } = useParams();
  const { state: routeState } = useLocation();

  useEffect(() => {
    if (isCurrentDosesRouteState(routeState)) {
      setAssessmentData(current => ({
        ...current,
        morphineDose: routeState.currentMorphineDose,
        clonidineDose: routeState.currentClonidineDose,
        clonidineFrequency: routeState.currentClonidineFrequency,
      }));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeState]);

  const { isOnline } = useNetworkCheck();

  const commonAssessment = React.createRef<CommonAssessmentType>();
  const mainAssessment = React.createRef<MainAssessmentType>();

  const questionnaireProgress = () => {
    if (!assessment) return 0;
    const lengthAssessment = LENGTH_COMMON_QUESTIONNAIRE + assessment.questions.length;

    return (stepsData.current / lengthAssessment) * 100;
  };

  const getLastStepData = (data: StepDataType): StepDataType => {
    // No history?
    if (data.completedOrdered.length < 1) {
      return { current: startStep, completedOrdered: [] };
    }
    // Has history, lets take last step
    const lastStep = data.completedOrdered[data.completedOrdered.length - 1];
    return { current: lastStep, completedOrdered: data.completedOrdered.slice(0, -1) };
  };

  const nextStep = (forceNextStep = false) => {
    if (forceNextStep) {
      setStepsData(data => {
        const currentStep = isOnline ? data.current + 1 : CommonQuestionSteps.HasPriorAssessment;
        return {
          current: currentStep,
          completedOrdered: [...data.completedOrdered, data.current],
        };
      });
      return;
    }

    const isOnCommonQuestionnaire = stepsData.current <= LENGTH_COMMON_QUESTIONNAIRE;
    const assessmentRef = isOnCommonQuestionnaire ? commonAssessment.current : mainAssessment.current;
    if (assessmentRef === null) return;

    const step = assessmentRef.handleNextStep();

    if (step === null) {
      if (isOnCommonQuestionnaire) {
        setStepsData(data => ({
          current: MAIN_ASSESSMENT_START_STEP,
          completedOrdered: [...data.completedOrdered, data.current],
        }));
      } else handleFinish();
    } else {
      setStepsData(data => ({
        current: step,
        completedOrdered: [...data.completedOrdered, data.current],
      }));
    }
  };

  const isNextStepDisabled = useCallback((): boolean => {
    switch (stepsData.current) {
      case CommonQuestionSteps.BirthWeight:
        return !assessmentData.birthWeightKg;
      case CommonQuestionSteps.BornDate:
        return !assessmentData.datetimeOfBirth;
      case CommonQuestionSteps.Transfer:
        return assessmentData.isTransferred === null;
      case CommonQuestionSteps.CurrentClonidineMorphineDose:
        return (
          assessmentData.hasEverHadClonidine === null ||
          assessmentData.hasEverHadMorphine === null ||
          (assessmentData.hasEverHadClonidine === true && assessmentData.clonidineDose === 0) ||
          (assessmentData.hasEverHadMorphine === true && assessmentData.morphineDose === 0)
        );
      case CommonQuestionSteps.LastKnowAssessment:
        return assessmentResult?.has_missing_assessment === null;
      case CommonQuestionSteps.ReasonForSkippedAssessment:
        return !assessmentResult?.missing_assessment_reason;
      case CommonQuestionSteps.HasPriorAssessment:
        return assessmentData.hasPriorAssessment === null;
      case CommonQuestionSteps.LastClonidineChange:
        return !assessmentData.changedClonidineDate;
      case CommonQuestionSteps.HasIncreasedClonidine:
        return assessmentData.clonidineIncreasedInLastSixHours === null;
      case CommonQuestionSteps.HasClonidineBeenTrialedOff:
        return assessmentData.hasClonidineBeenTrialedOff === null;
      case CommonQuestionSteps.LastMorphineChange:
        return !assessmentData.changedMorphineDate;
      case CommonQuestionSteps.LastMorphineRecommendation:
        return assessmentData.lastRecommendedMorphine === null;
      default:
        if (!assessmentResult.isComplete && stepsData.current > LENGTH_COMMON_QUESTIONNAIRE) {
          const relativeStep = stepsData.current - MAIN_ASSESSMENT_START_STEP;
          const currentQuestion = assessment?.questions[relativeStep];
          const currentAnswer = assessmentResult?.answers.find(answer => answer.question_id === currentQuestion?.id);
          return !currentAnswer;
        }
        return false;
    }
  }, [assessmentData, stepsData, assessment, assessmentResult]);

  const handleBackStep = () => {
    setStepsData(getLastStepData);
    if (assessmentResult.isComplete) {
      setAssessmentResult(v => ({ ...v, isComplete: false }));
    }
  };

  const handleFinish = () => {
    if (!assessment) return;

    // Once "complete", the results are no longer mutable
    if (assessmentResult?.isComplete) return;

    // Run through algorithm to calculate the result
    let algoResult: AssessProAlgoResult;
    try {
      algoResult = executeAssessProAlgorithm(assessment, assessmentData, assessmentResult.answers);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
        setIsErrorModalOpen(true);
      } else {
        console.error('An error occurred:', error);
      }
      return;
    }

    const missingAssessment = assessmentResult.missing_assessment_reason
      ? new MissingAssessmentRecord({
          changedClonidineDate: assessmentData.changedClonidineDate,
          changedMorphineDate: assessmentData.changedMorphineDate,
          lastRecommendedMorphine: assessmentData.lastRecommendedMorphine,
          clonidineIncreasedInLastSixHours: assessmentData.clonidineIncreasedInLastSixHours,
          hasClonidineBeenTrialedOff: assessmentData.hasClonidineBeenTrialedOff,
          hasMissingAssessment: assessmentResult.has_missing_assessment,
          missingAssessmentReason: assessmentResult.missing_assessment_reason,
          hasPriorAssessment: assessmentData.hasPriorAssessment,
          symptoms: assessmentData.symptoms,
          overallScore: determineEvaluationScore(assessmentData.symptoms),
        })
      : null;

    // Update assessment result, triggering useEffect to post results
    setAssessmentResult(current => {
      return {
        ...current,
        version: assessment?.version || 0,
        overall_score: algoResult.score,
        hours_old: algoResult.hoursSinceBirth || 0,
        has_ever_had_morphine: assessmentData.hasEverHadMorphine || assessmentData.morphineDose > 0,
        has_ever_had_clonidine: assessmentData.hasEverHadClonidine || assessmentData.clonidineDose > 0,
        has_clonidine_been_trialed_off: false, // || algoResult.clonidineResult?.isRestart,
        has_taken_long_acting_opioids:
          assessmentData.narcoticIds?.includes(Narcotics.Methadone) ||
          assessmentData.narcoticIds?.includes(Narcotics['Bupernophine (Subutex/Suboxone)']) ||
          false,
        assessment_result_symptom:
          algoResult.symptomScores.map(symptom => {
            return {
              symptom_id: symptom.symptom_id,
              score: symptom.score,
              is_poor: algoResult.activeSymptoms.includes(symptom.symptom_id),
            };
          }) || [],
        missing_assessment: missingAssessment,
        assessment_result_drug: [
          {
            drug_id: Drugs.Morphine,
            before_recommendation_dosage_mcg: assessmentData.morphineDose,
            before_recommendation_frequency: assessmentData.morphineDose > 0 ? 3 : 0,
            recommendation: algoResult.morphineResult.recommendation,
            recommendation_coded: MorphineRecommendation[algoResult.morphineResult.recommendationCoded],
            has_accepted_recommendation: false,
            decline_reasons: [],
            recommendation_dosage_mcg: algoResult.morphineResult.calculatedDose,
            recommendation_frequency: algoResult.morphineResult.calculatedDose > 0 ? 3 : 0,
            note: algoResult.morphineResult.note,
          },
          {
            drug_id: Drugs.Clonidine,
            before_recommendation_dosage_mcg: assessmentData.clonidineDose,
            before_recommendation_frequency: assessmentData.clonidineFrequency,
            recommendation: algoResult.clonidineResult.recommendation,
            recommendation_coded: ClonidineRecommendation[algoResult.clonidineResult.recommendationCoded],
            has_accepted_recommendation: false,
            decline_reasons: [],
            recommendation_dosage_mcg: algoResult.clonidineResult.calculatedDose,
            recommendation_frequency: algoResult.clonidineResult.frequency,
            note: algoResult.clonidineResult.note,
          },
        ],
        created: new Date(),
        isComplete: true, // Mark as complete. Triggers state changes.
      };
    });

    setStepsData(data => ({
      current: data.current + 1,
      completedOrdered: [...data.completedOrdered, data.current],
    }));
  };

  const fetchAssessment = useCallback(async () => {
    const defaultAssessmentVersion = 1;
    const version = user?.tenant_assessment_version ?? defaultAssessmentVersion;
    const { data }: AxiosResponse<AssessmentType> = await getAssessment(version);
    setAssessment(data);
  }, [setAssessment, user]);

  const offlineAssessment = useCallback(async () => {
    // Validate tenant key exists
    if (tenantKey === null) return;

    // Set offline data
    setAssessmentResult(current => {
      return {
        ...current,
        offline: {
          tenant_key: tenantKey,
          created_by: null,
        },
      };
    });
  }, [tenantKey, setAssessmentResult]);

  const fetchPatientData = useCallback(async () => {
    if (patientId) {
      const { data }: AxiosResponse<PatientMedicalRecord> = await getPatientInformation(patientId);

      setPatientMedicalData(new PatientMedicalRecord(data));
      setAssessmentData(current => {
        return {
          ...current,
          refId: data.ref_id || 0,
          patientId: data.ref_id,
          familyName: data.family_name,
          givenName: data.given_name,
          datetimeOfBirth: data.datetime_of_birth,
          hasEverHadClonidine: data.has_ever_had_clonidine,
          hasEverHadMorphine: data.has_ever_had_morphine,
          hasClonidineBeenTrialedOff: data.has_clonidine_been_trialed_off,
          hasTakenLongActingOpiods: data.has_taken_long_acting_opioids,
          narcoticIds: data.narcotic_ids,
          otherNarcotics: data.other_narcotics,
          birthWeightKg: data.birth_weight_kg,
        };
      });
      setAssessmentResult(current => {
        return {
          ...current,
          patient_id: patientId,
        };
      });
    }
  }, [patientId, setAssessmentData]);

  const fetchClonidineTrialedOff = useCallback(
    async (clonidineDose: number) => {
      if (isOnline && patientId) {
        const { data }: AxiosResponse<{ result: boolean }> = await postClonidineTrialedOffByPatientId(
          patientId,
          clonidineDose,
        );

        setAssessmentData(current => {
          return { ...current, hasClonidineBeenTrialedOff: data.result };
        });
      }
    },
    [patientId, setAssessmentData, isOnline],
  );

  const fetchCheckDrugChanges = useCallback(
    async (clonidineDose: number, clonidineFrequency: number, morphineDose: number) => {
      if (patientId) {
        const response: AxiosResponse<DrugChangesResultRecord> = await postCheckDrugChanges(
          patientId,
          clonidineDose,
          clonidineFrequency,
          morphineDose,
        );
        setAssessmentData(current => {
          return {
            ...current,
            changedClonidineDate: response.data[Drugs.Clonidine]?.changed ?? null,
            changedMorphineDate: response.data[Drugs.Morphine]?.changed ?? null,
            clonidineIncreasedInLastSixHours: null, // Mark null so algo knows to calculate from data below:
            lastClonidineDoseChangeDose: response.data[Drugs.Clonidine]?.before_recommendation_dose ?? null,
            lastClonidineDoseChangeFrequency: response.data[Drugs.Clonidine]?.before_recommendation_frequency ?? null,
          };
        });
      }
    },
    [patientId, setAssessmentData],
  );

  const fetchLastAssessmentResult = useCallback(async () => {
    if (patientId) {
      const response: AxiosResponse<AssessmentResultRecord> = await getLastAssessmentResultsByPatientId(patientId);

      // No results found
      if (response.status === 404) return null;

      return new AssessmentResultRecord(response.data);
    }
  }, [patientId]);

  const fetchAndSetAssessment = useCallback(async () => {
    try {
      if (isOnline) {
        setSubmitting(true);
        await Promise.all([fetchAssessment(), fetchPatientData()]);
        const lastResult = await fetchLastAssessmentResult();

        if (lastResult) {
          if (!isCurrentDosesRouteState(routeState)) {
            throw new Error('Current dose route state is required to start a follow-up assessment.');
          }

          const createdTime = dayjs(lastResult?.created);
          const currentTime = dayjs();
          const hoursDifference = currentTime.diff(createdTime, 'hour');
          const lastAssessmentClonidine = lastResult.assessment_result_drug.find(d => d.drug_id === Drugs.Clonidine);
          const lastAssessmentMorphine = lastResult.assessment_result_drug.find(d => d.drug_id === Drugs.Morphine);
          const lastAssessmentClonidineRecommendation =
            ClonidineRecommendation[
              lastAssessmentClonidine?.recommendation_coded as keyof typeof ClonidineRecommendation
            ];
          const lastAssessmentMorphineRecommendation =
            MorphineRecommendation[lastAssessmentMorphine?.recommendation_coded as keyof typeof MorphineRecommendation];
          const hasAssessmentGap = hoursDifference > MAX_HOURS_GAP_ASSESSMENT;

          const initialStep = hasAssessmentGap ? CommonQuestionSteps.LastKnowAssessment : MAIN_ASSESSMENT_START_STEP;
          setStepsData({ current: initialStep, completedOrdered: [] });
          setStartStep(initialStep);

          setAssessmentData(current => {
            return {
              ...current,
              isFirstAssessment: false,
              lastKnownAssessmentDate: lastResult.created,
              symptoms: lastResult.assessment_result_symptom.filter(s => s.is_poor).map(s => s.symptom_id),
              lastRecommendedMorphine: lastAssessmentMorphineRecommendation,
              isLastAssessmentRecommendationAnIncrease:
                lastAssessmentClonidineRecommendation === ClonidineRecommendation.Increase,
            };
          });

          await Promise.all([
            fetchClonidineTrialedOff(routeState.currentClonidineDose),
            fetchCheckDrugChanges(
              routeState.currentClonidineDose,
              routeState.currentClonidineFrequency,
              routeState.currentMorphineDose,
            ),
          ]);
        } else {
          setAssessmentData(current => {
            return {
              ...current,
              isFirstAssessment: lastResult === null,
            };
          });
          setStepsData({ current: CommonQuestionSteps.HasPriorAssessment, completedOrdered: [] });
          setStartStep(CommonQuestionSteps.HasPriorAssessment);
        }
      } else {
        await offlineAssessment();
        setAssessmentData(current => {
          return {
            ...current,
            refId: Number(patientId),
          };
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsErrorModalOpen(true);
    } finally {
      setSubmitting(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    patientId,
    assessmentData,
    isOnline,
    offlineAssessment,
    setAssessmentData,
    setStepsData,
    setSubmitting,
    fetchAssessment,
    fetchPatientData,
    fetchLastAssessmentResult,
    fetchClonidineTrialedOff,
    fetchCheckDrugChanges,
    LENGTH_COMMON_QUESTIONNAIRE,
  ]);

  const submitAssessment = useCallback(async () => {
    if (!assessmentResult?.isComplete) {
      return;
    }
    if (isOnline) {
      console.log('Posting assessment result:', assessmentResult);
      // Post results
      postAssessmentResult(assessmentResult).then(() => {
        // Reset
        // setAssessmentResult(new AssessmentResultRecord());
        setAssessmentData(new AssessmentDataRecord());
        // Route to patient page
        const route = replaceRouteParamWithValue(AppRoutes.CurrentPatient, 'id', patientId);
        navigate(route);
      });
    } else {
      db.assessmentResults.add({ ...assessmentResult, ref_id: patientId ?? '' }).then(() => {
        const route = replaceRouteParamWithValue(AppRoutes.CurrentPatient, 'id', patientId);
        navigate(route);
      });
    }
  }, [assessmentResult, patientId, navigate, isOnline]);

  useEffect(() => {
    fetchAndSetAssessment();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  useEffect(() => {
    if (patientMedicalData && assessmentData.isFirstAssessment && !isCurrentDosesRouteState(routeState)) {
      setAssessmentData(current => {
        return {
          ...current,
          morphineDose: patientMedicalData?.morphine_dosage_at_the_previous_hospital_mg || 0,
          clonidineDose: patientMedicalData?.clonidine_dosage_at_the_previous_hospital_mcg || 0,
          clonidineFrequency: patientMedicalData?.clonidine_frequency_at_the_previous_hospital || 0,
        };
      });
    }
  }, [assessmentData.isFirstAssessment, patientMedicalData, routeState]);

  useEffect(() => {
    if (assessmentFromDB && assessment) {
      const isAssessmentFromDBEmpty = !assessmentFromDB?.length;
      const isVersionDifferent = assessmentFromDB?.[0]?.version !== assessment.version;

      if (isAssessmentFromDBEmpty || isVersionDifferent) {
        db.assessment.clear().then(() => {
          db.assessment.add({
            version: assessment.version,
            comfort_recommendations: assessment.comfort_recommendations,
            questions: assessment.questions,
          });
        });
      }
    }

    if (!isOnline && !assessment) {
      if (assessmentFromDB && assessmentFromDB.length) {
        setAssessment(assessmentFromDB[0]);
      }
    }
  }, [assessmentFromDB, assessment, isOnline]);

  useEffect(() => {
    if (!isOnline && assessmentFromDB !== undefined && !assessmentFromDB.length) {
      setOfflineWarningModalOpen(true);
    }
  }, [assessmentFromDB, isOnline]);

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setErrorMessage('');
  };

  const closeOfflineWarningModal = () => {
    setOfflineWarningModalOpen(false);
    navigate(AppRoutes.Login);
  };

  const contextValue = useMemo(
    () => ({
      currentStep: stepsData.current,
      assessment,
      assessmentData,
      assessmentResult,
      setAssessmentData,
      setAssessmentResult,
    }),
    [assessment, assessmentData, setAssessmentData, assessmentResult, setAssessmentResult, stepsData],
  );

  if (isSubmitting) {
    return (
      <div className="flex items-center h-[calc(100vh-174px)]">
        <Loader />
      </div>
    );
  }

  return (
    <AssessmentContext.Provider value={contextValue}>
      <div className="py-3 px-5 md:py-6 md:px-10 h-[calc(100vh-174px)] overflow-y-auto">
        <div className="w-full max-w-[768px] mx-auto">
          <AssessmentHeader refId={assessmentData?.refId} isFirstAssessment={assessmentData.isFirstAssessment} />
          {stepsData.current <= LENGTH_COMMON_QUESTIONNAIRE && (
            <CommonQuestionnaire
              ref={commonAssessment}
              relativeStep={stepsData.current - 1}
              proceedToNextStep={nextStep}
            />
          )}
          {stepsData.current > LENGTH_COMMON_QUESTIONNAIRE && !assessmentResult.isComplete && (
            <MainAssessmentQuestionnaire
              ref={mainAssessment}
              relativeStep={stepsData.current - MAIN_ASSESSMENT_START_STEP}
              proceedToNextStep={nextStep}
            />
          )}
          {assessmentResult.isComplete && (
            <ReviewAssessmentStep
              assessmentResult={
                {
                  ...assessmentResult,
                  id: '0',
                  comfortRecommendations: [],
                  created: new Date().toISOString(),
                } as AssessmentResultType
              }
            />
          )}
        </div>
      </div>

      <div className="sticky bottom-0 px-5 md:px-10 py-6 flex justify-between items-center bg-white-300 dark:bg-blue-500 border-t-[1px] border-gray-200 dark:border-white-400/10">
        <BaseButton
          className={cn({ 'opacity-0 cursor-not-allowed pointer-events-none': startStep === stepsData.current })}
          disabled={stepsData.current === startStep}
          scheme="cta-secondary"
          size="xl"
          isIconOnLeftSide
          icon={LeftIcon}
          onClick={handleBackStep}
        >
          Back
        </BaseButton>
        <div className="w-full max-w-[160px] hidden sm:flex">
          <ProgressBar progress={questionnaireProgress()} />
        </div>
        <div className="flex gap-3 items-center">
          <BaseButton
            scheme="cta-primary"
            size="xl"
            icon={RightIcon}
            disabled={isNextStepDisabled()}
            onClick={() => (assessmentResult.isComplete ? submitAssessment() : nextStep())}
          >
            {assessmentResult.isComplete ? 'Submit' : 'Next'}
          </BaseButton>
        </div>
      </div>

      {isErrorModalOpen && (
        <ErrorModal onCancelClick={closeErrorModal} onRetryClick={fetchAssessment} errorMessage={errorMessage} />
      )}
      {isOfflineWarningModalOpen && (
        <MessageModal
          icon={AlertIcon}
          iconProps={{ className: 'text-red-400' }}
          isShownCloseButton={false}
          successText="Back to login"
          cancelText="Cancel"
          title="Sorry, but the assessment requires the device to be online to receive basic assessment questions"
          description="Please check your device settings and try again."
          onSuccessClick={closeOfflineWarningModal}
        />
      )}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = (): AssessmentContextType => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
};
